import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import {
    AddressBalanceMap,
    ExcludeTxBodyType,
    FindType,
    MoneyFlow,
    MoneyFlowVersions,
    NewMoneyFlowProps,
    TxsByCurrency
} from '../../backend/types/moneyflow.ts'
import { Document } from '../../backend/types/document.ts'
import { handleResponse } from '../handleResponse.ts'
import { mutationHandler } from "../mutationHandler.ts"
import { TableParams } from '../../types/tableParams.ts'
import { PaginatedTxsWithPriceType } from '../../components/TransactionTable/transactionTable.tsx'
import { Token } from '../../backend/server/modules/tokens.ts'
import { ShareableLink } from '../../backend/types/shareableLink.ts'

export const useGetMoneyFlows = () => useQuery <Document<MoneyFlow>[]>({
    queryKey: [`moneyflow`]
})

export const useAllMoneyflowCurrencies = (moneyFlowId: string) => useQuery<Token[]>({
    queryKey: [`moneyflow/${moneyFlowId}/get-all-currencies`]
})

export const useGetTokensList = (enabled: boolean, blockchain: string) => useQuery<Token[]>({
    queryKey: ['moneyflow/tokenslist?blockchain=' + blockchain],
    enabled
})

export const useGetMoneyflowVersions = (moneyFlowId: string) => useQuery<MoneyFlowVersions[]>({
    queryKey: [`moneyflow/${moneyFlowId}/versions`],
    enabled: !!moneyFlowId
})

export const useFindEntityIdByAddress = (moneyFlowId: string, address: string[], step: number) =>
    useQuery<FindType<'entity'>[]>({
        queryKey: [`moneyflow/${moneyFlowId}/find-address/${address.join(',')}?step=${step}`],
        enabled: !!address.length
    })

interface UseFindEdgeByTxHash {
    foundEdges: FindType<'edge'>[],
    notFoundTx: string[]
}

export const useFindEdgeByTxHash = (moneyFlowId: string, txhash: string[], step: number) =>
    useQuery<UseFindEdgeByTxHash>({
        queryKey: [`moneyflow/${moneyFlowId}/find-tx/${txhash.join(',')}?step=${step}`],
        enabled: !!txhash.length
    })

export const excludeTransactions = async (body: ExcludeTxBodyType) =>
    fetch(`/api/moneyflow/excludeTxs`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body)
    })

export const getEntityTxs = async (
    entityId: string,
    moneyFlowId: string,
    direction: 'inbound' | 'outbound' | undefined,
    step: number,
    params: TableParams
): Promise<PaginatedTxsWithPriceType> =>
    handleResponse(await fetch(`/api/moneyflow/${moneyFlowId}/entitytxs/${entityId}?${direction ? `${direction}=1&` : ''}step=${step}`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...params })
    }))

export const getEntityTxsByCurrency = async (
    entityId: string, moneyFlowId: string, step: number, params: TableParams
): Promise<TxsByCurrency> =>
    handleResponse(await fetch(`/api/moneyflow/${moneyFlowId}/enttxsbc/${entityId}?step=${step}`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...params })
    }))

export const getEdgeTxsByCurrency = async (
    edgeId: string, moneyFlowId: string, step: number, params: TableParams
): Promise<TxsByCurrency> =>
    handleResponse(await fetch(`/api/moneyflow/${moneyFlowId}/edgtxsbc/${edgeId}?step=${step}`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...params })
    }))
export const getEdgeTxs = async (
    edgeId: string, moneyFlowId: string, step: number, params: TableParams): Promise<PaginatedTxsWithPriceType> =>
    handleResponse(await fetch(`/api/moneyflow/${moneyFlowId}/edgetxs/${edgeId}?step=${step}`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...params })
    }))

const createMoneyFlow = (diagram: NewMoneyFlowProps) => fetch(`/api/moneyflow`, {
    method: 'POST',
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify(diagram)
})

export const useExlcudeTxsMutation = (initialMoneyflowID: string, callback: (data: string) => void) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationKey: ['excludeTxs'],
        mutationFn: (txsBody: ExcludeTxBodyType) => mutationHandler<string>(excludeTransactions(txsBody)),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [`moneyflow/${initialMoneyflowID}`] })
            queryClient.invalidateQueries({ queryKey: [`moneyflow/${initialMoneyflowID}/versions`] })
            callback(data)
        }
    })
}

export const useCreateMoneyFlowMutation = (
    onCreation: (moneyFlow: Document<MoneyFlow>) => void
) => useMutation({
    mutationKey: ['moneyflow'],
    mutationFn: mutationHandler<Document<MoneyFlow>, typeof createMoneyFlow>(createMoneyFlow),
    onSuccess: (data: Document<MoneyFlow>) => {
        onCreation(data)
    },
})

const updateDiagram = (diagramId: string, diagram: MoneyFlow) => fetch(`/api/moneyflow/${diagramId}`, {
    method: 'PUT',
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify(diagram)
})

export const useUpdateDiagramMutation = (caseId: string, documentId: string, diagramId: string | undefined) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['moneyflow', caseId, diagramId],
        mutationFn: (diagram: MoneyFlow) => mutationHandler<Document<MoneyFlow>>(updateDiagram(diagramId!, diagram)),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [documentId] })
    })
}

const getAddressesBalances = (
    tracingId: string | undefined, moneyflowId: string, entityId: string, step: number
) =>
    fetch(`/api/moneyflow/${moneyflowId}/addresses_balances`, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            step,
            tracingId,
            entityId
        })
    })

export const useGetAddressesBalances = (
    entityId: string,
    moneyflowId: string,
    tracingId: string | undefined,
    step: number) =>
    useQuery<AddressBalanceMap>({
        queryKey: [`${tracingId}_${entityId}_${step}`],
        staleTime: 1000 * 60 * 5,
        retry: 1,
        queryFn: () => getAddressesBalances(tracingId, moneyflowId, entityId, step)
            .then(handleResponse<AddressBalanceMap>),
        enabled: !!tracingId
    })

export const useGetSharedLinks = () => useQuery<ShareableLink[]>({
    queryKey: ['/public/shared-links']
})
